/** @jsx jsx */
import React, { type ReactNode, useRef, useEffect } from 'react';

import { css, jsx } from '@compiled/react';
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import { token } from '@atlaskit/tokens';
import { useShowUFOSegmentsState } from './hooks/useShowUFOSegmentsState.tsx';

type Props = {
	name: string;
	children: ReactNode;
	mode?: 'list' | 'single';
	isThirdPartySegment?: boolean;
	// this is being used by FF inspector tool - will be converted to data-path attribute
	'data-path'?: string;
};
/**
 * @description Used only on staging with jira_highlight_ufo_segments fg to see ufo segments
 *  Do not import directly!! only import behind importCond so that it does not have impact in production
 */
export const DEBUGUFOSegmentHighlight = (props: Props): JSX.Element => {
	const [showUFOSegments] = useShowUFOSegmentsState();

	// Always add data-boundary when FG is on, but only show visual highlights when showUFOSegments is true
	if (!showUFOSegments) {
		if (props.isThirdPartySegment) {
			return <ThirdPartyUfoSegmentWrapperWithBoundary {...props} />;
		}
		return <UfoSegmentWrapperWithBoundary {...props} />;
	}
	if (props.isThirdPartySegment) {
		return <ThirdPartyUfoSegmentWrapperWithHighlight {...props} />;
	}
	return <UfoSegmentWrapperWithHighlight {...props} />;
};
/**
 * used only on staging with jira_highlight_ufo_segments fg to add data-boundary without visual styling
 * Uses display: contents to avoid layout impact
 */
const ThirdPartyUfoSegmentWrapperWithBoundary = ({
	children,
	name,
	'data-path': dataPath,
}: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const parent = containerRef.current.parentElement;
		const hasPageLayoutRoot = parent?.matches('[id="unsafe-design-system-page-layout-root"]');

		if (!hasPageLayoutRoot) {
			return;
		}

		// Whatever we render, if it is a immediate child of PageLayout (Root), it needs to have
		// the data-layout-slot attribute for it to be visible.
		containerRef.current.setAttribute('data-layout-slot', 'true');
	}, []);

	return (
		<div
			data-boundary="true"
			data-ufo-segment={name}
			data-third-party-segment="true"
			css={invisibleWrapperStyles}
			data-path={dataPath}
			ref={containerRef}
		>
			{children}
		</div>
	);
};

/**
 * used only on staging with jira_highlight_ufo_segments fg to add data-boundary without visual styling
 * Uses display: contents to avoid layout impact
 */
const UfoSegmentWrapperWithBoundary = ({ children, name, 'data-path': dataPath }: Props) => {
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!containerRef.current) {
			return;
		}

		const parent = containerRef.current.parentElement;
		const hasPageLayoutRoot = parent?.matches('[id="unsafe-design-system-page-layout-root"]');

		if (!hasPageLayoutRoot) {
			return;
		}

		// Whatever we render, if it is a immediate child of PageLayout (Root), it needs to have
		// the data-layout-slot attribute for it to be visible.
		containerRef.current.setAttribute('data-layout-slot', 'true');
	}, []);

	return (
		<div
			data-boundary="true"
			data-ufo-segment={name}
			css={invisibleWrapperStyles}
			data-path={dataPath}
			ref={containerRef}
		>
			{children}
		</div>
	);
};

/**
 * used only on staging with jira_highlight_ufo_segments fg to see ufo segments
 */
const ThirdPartyUfoSegmentWrapperWithHighlight = ({
	children,
	name,
	'data-path': dataPath,
}: Props) => {
	const thirdPartySegmentName = `thirdPartySegment ${name}`;
	return (
		<Box
			xcss={borderStylesBlack}
			data-boundary="true"
			data-ufo-segment={name}
			data-third-party-segment="true"
			data-path={dataPath}
		>
			<span css={textStylesBlack}>{thirdPartySegmentName}</span>
			{children}
		</Box>
	);
};

/**
 * used only on staging with jira_highlight_ufo_segments fg to see ufo segments
 */
const UfoSegmentWrapperWithHighlight = ({ children, name, 'data-path': dataPath }: Props) => {
	return (
		<Box xcss={borderStylesBlue} data-boundary="true" data-ufo-segment={name} data-path={dataPath}>
			<span css={textStylesBlue}>{name}</span>
			{children}
		</Box>
	);
};

const textStylesBlue = css({
	position: 'relative',
	color: token('color.text.accent.blue'),
	display: 'flex',
	justifyContent: 'center',
});

const borderStylesBlue = xcss({
	border: `${token('border.width.selected')} solid blue`,
	borderRadius: 'radius.small',
	padding: 'space.100',
});

const textStylesBlack = css({
	position: 'relative',
	color: token('color.text'),
	display: 'flex',
	justifyContent: 'center',
});

const borderStylesBlack = xcss({
	border: `${token('border.width.selected')} solid black`,
	borderRadius: 'radius.small',
	padding: 'space.100',
});

// CSS for invisible wrapper (no layout impact)
const invisibleWrapperStyles = css({
	display: 'contents', // Makes wrapper invisible to layout
});
